/**
 * Board travel chips and the bulk backlog preview (tracker item 41, phase 4).
 * Pure helpers, unit-tested in travelUi.test.ts. The backend sends, per
 * technician and day, the drives between consecutive timed jobs
 * (`board.travel`) and each job's `location` (null = no map location yet).
 */

import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { formatDayHeading, formatDuration, saveScheduleChange } from './dispatchUi';
import { suggestionOrigin } from './suggestionUi';

/** Height of a travel chip on the day grid (px). */
export const TRAVEL_CHIP_HEIGHT = 18;

const unitLabel = (unit: unknown) => (unit === 'KM' ? 'km' : 'mi');
const KM_PER_MILE = 1.609344;

/** A distance in the company unit, one decimal ("4.3 mi"). */
export function distanceText(km: number, unit: unknown): string {
  const value = unit === 'KM' ? km : km / KM_PER_MILE;
  return `${Math.round(value * 10) / 10} ${unitLabel(unit)}`;
}

/** The legs of one technician's day, keyed by the job they drive to. */
export function travelLegsByJob(
  board: Pick<types.DispatchBoard, 'travel'>,
  userId: string | null,
  date: string
): Map<string, types.BoardTravelLeg> {
  const day = userId ? board.travel?.find((d) => d.userId === userId && d.date === date) : undefined;
  return new Map((day?.legs ?? []).map((leg) => [leg.toJobId, leg]));
}

/** Chip text: "~12 min", "Same place", or "~15 min?" when a location is missing (estimated). */
export function travelChipText(leg: types.BoardTravelLeg): string {
  if (leg.unknown) return `~${leg.minutes} min?`;
  if (leg.km === 0) return 'Same place';
  return `~${leg.minutes} min`;
}

/** The chip's accessible description (also its tooltip). */
export function travelChipLabel(leg: types.BoardTravelLeg, unit: unknown): string {
  const from = leg.fromJobId ? 'the previous job' : 'the company address';
  if (leg.unknown) return `Drive from ${from}: about ${leg.minutes} min, estimated (no map location for one end)`;
  if (leg.km === 0) return `Drive from ${from}: same place, no drive`;
  return `Drive from ${from}: about ${leg.minutes} min, ${distanceText(leg.km ?? 0, unit)} straight line`;
}

/** Total drive time of a technician's day (the first leg from the company address included). */
export function dayTravelSummary(board: Pick<types.DispatchBoard, 'travel'>, userId: string | null, date: string): string | null {
  const day = userId ? board.travel?.find((d) => d.userId === userId && d.date === date) : undefined;
  if (!day || day.legs.length === 0) return null;
  const minutes = day.legs.reduce((sum, leg) => sum + leg.minutes, 0);
  const estimated = day.legs.some((leg) => leg.unknown);
  return `~${formatDuration(minutes)} driving${estimated ? ' (estimated)' : ''}`;
}

/**
 * Where to draw a chip between two consecutive cards on the day grid: in the
 * gap when it is tall enough, otherwise straddling the next card's top edge.
 */
export function travelChipTop(previousBottom: number, nextTop: number, chipHeight = TRAVEL_CHIP_HEIGHT): number {
  const gap = nextTop - previousBottom;
  if (gap >= chipHeight) return previousBottom + Math.floor((gap - chipHeight) / 2);
  return nextTop - Math.ceil(chipHeight / 2);
}

/** True when the board says the job's property has no coordinates (undefined = older data, no marker). */
export function locationUnknown(job: Pick<types.BoardJob, 'location'>): boolean {
  return job.location === null;
}

/*
 * Bulk backlog preview.
 */

/** Most jobs in one bulk preview (the backend cap, also the backlog page). */
export const BULK_SUGGEST_MAX = 25;

/** The visible backlog rows a bulk preview covers: schedulable ones, at most 25, in the order shown. */
export function bulkCandidates(backlog: types.BoardJob[]): types.BoardJob[] {
  return backlog.filter((job) => job.schedulable).slice(0, BULK_SUGGEST_MAX);
}

/** Why a job was not placed. */
export function unplacedText(placement: Pick<types.BulkPlacement, 'reason'>): string {
  switch (placement.reason) {
    case 'OUTSIDE_HORIZON': return 'Its date is outside these days (try "Any date")';
    case 'NOT_SCHEDULABLE': return 'Finished or cancelled; it cannot be scheduled';
    case 'NO_SLOT': return 'No free slot fits it after the jobs above';
    default: return 'Not placed';
  }
}

/** "jane@example.com · Tue 4 Mar · 8:00 AM - 9:00 AM" style line for a placement. */
export function placementText(candidate: types.SuggestionCandidate, timeText: string): string {
  return `${candidate.technicianEmail} · ${formatDayHeading(candidate.date)} · ${timeText}`;
}

export interface BulkApplyResult {
  applied: number;
  /** The slot was taken meanwhile (409 SUGGESTION_STALE or a pre-check conflict) */
  stale: number;
  /** Someone else changed the job (409 JOB_CHANGED) */
  changed: number;
  /** Any other error */
  failed: number;
}

/**
 * Apply placements one at a time through the normal schedule path
 * (POST /check, then PATCH with expectedUpdatedAt, requireNoConflicts and
 * the AUTO_ASSIGN origin). A placement that went stale or whose job changed
 * is skipped and counted; the others still apply (each is re-checked by the
 * server against the real day). Never offers a warning override.
 */
export async function applyBulkPlacements(
  api: Pick<IDispatchApi, 'checkJobSchedule' | 'scheduleJob'>,
  placements: types.BulkPlacement[],
  jobs: Map<string, Pick<types.BoardJob, 'id' | 'status' | 'scheduledDate' | 'scheduledStartTime' | 'assignedTo' | 'arrivalWindowMinutes'>>,
  onProgress?: (done: number, total: number) => void
): Promise<BulkApplyResult> {
  const result: BulkApplyResult = { applied: 0, stale: 0, changed: 0, failed: 0 };
  const todo = placements.filter((p) => p.candidate);
  for (const [index, placement] of todo.entries()) {
    const candidate = placement.candidate!;
    const job = jobs.get(placement.jobId);
    try {
      const outcome = await saveScheduleChange(
        api,
        {
          id: placement.jobId,
          status: job?.status ?? 'DRAFT',
          updatedAt: placement.expectedUpdatedAt,
          scheduledDate: job?.scheduledDate ?? null,
          scheduledStartTime: job?.scheduledStartTime ?? null,
          assignedTo: job?.assignedTo ?? null,
          arrivalWindowMinutes: job?.arrivalWindowMinutes ?? null,
        },
        { scheduledDate: candidate.date, scheduledStartTime: candidate.startTime, assignedTo: candidate.technicianId },
        () => false,
        { origin: suggestionOrigin(candidate) }
      );
      if (outcome.status === 'saved') result.applied += 1;
      else if (outcome.status === 'stale') result.stale += 1;
      else if (outcome.status === 'changed') result.changed += 1;
      else result.failed += 1;
    } catch {
      result.failed += 1;
    }
    onProgress?.(index + 1, todo.length);
  }
  return result;
}

/** The notice after a bulk apply. */
export function bulkAppliedMessage(result: BulkApplyResult): string {
  const parts = [`Scheduled ${result.applied} job${result.applied === 1 ? '' : 's'}.`];
  const skipped = result.stale + result.changed;
  if (skipped > 0) parts.push(`${skipped} skipped because the slot was taken or the job changed meanwhile.`);
  if (result.failed > 0) parts.push(`${result.failed} could not be saved.`);
  return parts.join(' ');
}
